import { Button } from '@grofers/react';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { grey } from 'sushiweb/tokens/color';
import Spinner from 'sushiweb/Spinner/Spinner';
import { InfoLine } from 'sushiweb/Icons';
import { Star } from '../../../../../UiKit/Star/Star';
import { componentMap } from '../../../../../components/serverDrivenUIComponents/compMapping';
import { useCreateNode } from '../apis';
import Multiselect from 'multiselect-react-dropdown';
import '../HelpSectionForms/HelpSectionFormCss.scss';
import { Tooltip } from '../../../../../UiKit';

const FieldComp = ({
  field,
  dataToSend,
  updateDataToSend,
  isMultiSelect = false,
}) => {
  const multiselectRef = useRef();
  const saveOptions = useCallback(
    (key, value) => {
      let multiSelectedValues = [];
      if (isMultiSelect) {
        if (
          dataToSend[field?.id]?.length &&
          !dataToSend[field?.id].includes(value)
        ) {
          multiSelectedValues = [...dataToSend[field?.id], value];
        } else {
          multiSelectedValues = [value];
        }
        value = multiSelectedValues;
      }
      updateDataToSend(key, value);
    },
    [isMultiSelect, updateDataToSend, dataToSend, field?.id],
  );

  // applicable for multiselectable select input
  const unSaveOption = useCallback(
    (key, valueDeSelected) => {
      const selectedValues = dataToSend[field?.id];
      let updatedValues = [];
      for (let i = 0; i < selectedValues.length; i++) {
        const item = selectedValues[i];
        if (
          field?.type === 'combo_select_integer' &&
          item.select === valueDeSelected.select &&
          item.value === valueDeSelected.value
        )
          continue;
        if (
          field?.type === 'combo_duo_select_integer' &&
          item.selectOne === valueDeSelected.selectOne &&
          item.selectTwo === valueDeSelected.selectTwo &&
          item.value === valueDeSelected.value
        )
          continue;
        if (item == valueDeSelected) continue;
        updatedValues = [...updatedValues, item];
      }
      updateDataToSend(key, updatedValues);
    },
    [dataToSend, field?.id, updateDataToSend],
  );

  let CompName = null;
  if (field.type in componentMap) CompName = componentMap[`${field.type}`];

  return (
    <div className='tw-flex tw-flex-col tw-h-max  tw-flex-1 tw-gap-2'>
      <div className='tw-flex tw-gap-2 tw-w-full'>
        {field.info?.rules?.length ? (
          <Tooltip
            position='bottom'
            border={false}
            title={<InfoLine size={14} color='#d02a38' />}
            widthMaxContent={false}
            labelWidth='50'
            label={() => {
              return (
                <div className='tw-flex tw-flex-col tw-flex-wrap tw-gap-2 dropdown'>
                  {field.info.rules.map((rule, index) => {
                    return (
                      <div
                        className='tw-flex tw-gap-2 tw-text-100 tw-font-regular'
                        key={`helpdesk_form_field_info_${index}`}
                      >
                        <span className='tw-text-300 tw-font-regular'>{`${
                          index + 1
                        }. `}</span>
                        <span className='tw-text-300 tw-font-regular'>
                          {rule}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            }}
          />
        ) : null}

        <div className=' tw-color-[#1F1F1F] tw-flex tw-gap-1 tw-items-center'>
          <span className='tw-tex-300 tw-font-semibold'>{field?.label}</span>
          {field.info?.rule_type ? (
            <span className='tw-tex-200 tw-font-medium tw-ml-1'>{`(${field.info.rule_type})`}</span>
          ) : null}
          <span>{field?.required && <Star icon='*' />}</span>
        </div>
      </div>
      <div
        className='tw-flex tw-flex-col tw-w-full'
        id='helpdesk-create-node-form'
        css={`
          .rdw-list-dropdown,
          .rdw-text-align-dropdown {
            z-index: 0 !important;
          }
        `}
      >
        {field?.isMultiSelect && field?.type === 'select' ? (
          <Multiselect
            showCheckbox
            ref={multiselectRef}
            avoidHighlightFirstOption
            singleSelect={false}
            loading={false}
            placeholder={`Search ${field?.label}`}
            options={field?.options}
            onSelect={(selectedList, selectedItem) => {
              const key = Object.keys(selectedItem)
                ? Object.keys(selectedItem)[0]
                : null;
              const value = key ? selectedItem[key] : null;
              saveOptions(field?.id, value);
            }}
            onRemove={(selectedList, removedItem) => {
              const key = Object.keys(removedItem)
                ? Object.keys(removedItem)[0]
                : null;
              const value = key ? removedItem[key] : null;
              unSaveOption(field?.id, value);
            }}
            displayValue='name'
          />
        ) : (
          <CompName
            field={field}
            inputKey={field?.id}
            selectOptions={field?.options || field?.optionsOne}
            selectOptionsTwo={field?.optionsTwo}
            saveOptions={saveOptions}
            unSaveOptions={unSaveOption}
            dataToSend={dataToSend}
            isEditable={field?.isEditable}
            isMultiSelect={field?.isMultiSelect}
            defaultValue={field?.value}
          />
        )}
      </div>
    </div>
  );
};

export const CreateNodeForm = ({
  formFields,
  ruleList = [],
  rootId = null,
  refetchRootNodes = () => {},
  closeForm = () => {},
}) => {
  const [dataToSend, setDataToSend] = useState({});
  const { mutate: createRootNode, isLoading } = useCreateNode(
    {
      onSuccess: () => {
        closeForm();
      },
    },
    refetchRootNodes,
  );

  useEffect(() => {
    let data = {};
    for (let i = 0; i < formFields?.length; i++) {
      const field = formFields[i];

      if (field?.value || field?.value === 0) {
        data = {
          ...data,
          [field?.id]: field?.value,
        };
      }
    }
    setDataToSend(data);
  }, [formFields]);

  const convertRuleIdsToMap = (dataToSend) => {
    let updatedDataToSend = {};
    let rule_map = {};

    Object.keys(dataToSend).forEach((key) => {
      if (ruleList.includes(key))
        rule_map = {
          ...rule_map,
          [key]: Array.isArray(dataToSend[key])
            ? dataToSend[key]
            : [dataToSend[key]],
        };
      else {
        updatedDataToSend = {
          ...updatedDataToSend,
          [key]: dataToSend[key],
        };
      }
    });

    updatedDataToSend = {
      ...updatedDataToSend,
      rule_map: {
        ...rule_map,
      },
    };
    return updatedDataToSend;
  };

  const onSubmit = useCallback(
    (e) => {
      e.preventDefault();
      dataToSend.desk_name = 'customer';
      const finalPayload = convertRuleIdsToMap(dataToSend);
      createRootNode(finalPayload);
    },
    [rootId, dataToSend],
  );

  const updateDataToSend = useCallback(
    (key, value) => {
      const data = {
        ...dataToSend,
        [key]: value,
      };
      setDataToSend(data);
    },
    [dataToSend],
  );

  return (
    <form
      className='tw-w-full tw-flex tw-flex-col tw-h-max tw-gap-10 tw-bg-white tw-rounded-2xl tw-pb-36 tw-mb-[20rem]'
      onSubmit={onSubmit}
    >
      <div className='tw-flex-col tw-gap-6 tw-px-8'>
        {formFields.map((field) => {
          if (field?.isVisible === undefined || field?.isVisible)
            return (
              <FieldComp
                field={field}
                ruleList={ruleList}
                dataToSend={dataToSend}
                updateDataToSend={updateDataToSend}
                isMultiSelect={field?.isMultiSelect}
              />
            );
        })}
      </div>
      <div
        className='tw-w-2/5 tw-bg-base-white tw-py-6 tw-px-5 tw-flex tw-justify-end tw-fixed tw-bottom-0 tw-z-[2000] tw-right-0 tw-gap-5'
        css={`
          box-shadow: 0px 0px 16px 0px rgba(28, 28, 28, 0.12);
        `}
      >
        <Button variant={'fill'} className='tw-h-14 tw-z-50 tw-w-28'>
          {isLoading ? (
            <Spinner size='small' color={`${grey.z300}`} />
          ) : (
            'Submit'
          )}
        </Button>
      </div>
    </form>
  );
};
